import { useLayoutEffect, useMemo, useRef, useState } from 'react'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { DownloadSimple as DownloadIcon } from '@phosphor-icons/react/dist/ssr/DownloadSimple'
import { Image as ImageIcon } from '@phosphor-icons/react/dist/ssr/Image'

import type { CabinetAffinityPair, CabinetMapData } from '@/lib/assistant-cabinet-heatmap'
import {
	formatHeatmapRevenue,
	heatmapCellBackground,
	heatmapCellTextColor,
	pairCellBackground,
	pairPlacementLabel,
	suggestMoveLabel,
} from '@/lib/assistant-cabinet-heatmap'
import { captureElementAsPng, downloadCsv } from '@/lib/assistant-export'

interface ChatCabinetHeatmapProps {
	data: CabinetMapData
}

type MapMetric = 'revenue' | 'units' | 'pairs' | 'issues' | 'swaps'

interface LineSegment {
	key: string
	x1: number
	y1: number
	x2: number
	y2: number
	color: string
	dashed: boolean
}

function truncateProductName(name: string, max = 14): string {
	if (name.length <= max) return name
	return `${name.slice(0, max - 1)}…`
}

function cellMetricValue(cell: CabinetMapData['rows'][number][number], metric: MapMetric): number {
	return metric === 'revenue' ? cell.revenueCents : cell.units
}

function cellMetricLabel(cell: CabinetMapData['rows'][number][number], metric: MapMetric): string {
	if (metric === 'revenue') return formatHeatmapRevenue(cell.revenueCents)
	return `${cell.units} unit${cell.units === 1 ? '' : 's'}`
}

function defaultMetric(data: CabinetMapData): MapMetric {
	if (data.swaps.length) return 'swaps'
	if (data.stockoutCount > 0 || data.visionWarnings.length) return 'issues'
	if (data.pairs.length && data.maxRevenueCents === 0 && data.maxUnits === 0) return 'pairs'
	return 'revenue'
}

function PairLegendItem({ pair }: { pair: CabinetAffinityPair }) {
	const slotA = pair.productA.slotLabel ?? '—'
	const slotB = pair.productB.slotLabel ?? '—'
	const hint = suggestMoveLabel(pair.suggestMove)
	const label = `${truncateProductName(pair.productA.productName, 18)} + ${truncateProductName(pair.productB.productName, 18)}`

	return (
		<Stack direction="row" spacing={0.75} alignItems="flex-start">
			<Box
				sx={{
					width: 10,
					height: 10,
					borderRadius: '50%',
					bgcolor: pair.color,
					flexShrink: 0,
					mt: 0.35,
				}}
			/>
			<Box sx={{ minWidth: 0 }}>
				<Typography sx={{ fontSize: '0.6875rem', lineHeight: 1.35 }} variant="body2">
					{label}
				</Typography>
				<Typography color="text.secondary" sx={{ fontSize: '0.625rem', lineHeight: 1.35 }} variant="caption">
					{slotA} ↔ {slotB} • lift {pair.lift.toFixed(1)} • {pair.cooccurOrders} orders •{' '}
					{pairPlacementLabel(pair.placement)}
					{hint ? ` • ${hint}` : ''}
				</Typography>
			</Box>
		</Stack>
	)
}

export function ChatCabinetHeatmap({ data }: ChatCabinetHeatmapProps) {
	const [metric, setMetric] = useState<MapMetric>(() => defaultMetric(data))
	const gridRef = useRef<HTMLDivElement>(null)
	const cellRefs = useRef<Map<number, HTMLDivElement>>(new Map())
	const [lines, setLines] = useState<LineSegment[]>([])

	const maxValue = metric === 'revenue' ? data.maxRevenueCents : data.maxUnits
	const showPairsMode = data.pairs.length > 0
	const showIssuesMode = data.stockoutCount > 0 || data.lowStockCount > 0 || data.visionWarnings.length > 0
	const showSwapsMode = data.swaps.length > 0
	const hasSales = data.maxRevenueCents > 0 || data.maxUnits > 0

	const subtitle = useMemo(() => {
		const parts: string[] = []
		if (data.siteName) parts.push(data.siteName)
		if (data.dateFrom && data.dateTo) parts.push(`${data.dateFrom} → ${data.dateTo}`)
		return parts.join(' • ')
	}, [data.dateFrom, data.dateTo, data.siteName])

	const title =
		metric === 'swaps' && showSwapsMode
			? 'Swap preview map'
			: metric === 'issues' && showIssuesMode
				? 'Stock & vision map'
				: metric === 'pairs' && showPairsMode
					? 'Product pair map'
					: showPairsMode || showIssuesMode || showSwapsMode
						? 'Cabinet sales map'
						: 'Cabinet sales heatmap'

	const exportRef = useRef<HTMLDivElement>(null)

	const handleExportCsv = () => {
		const rows = [['Slot', 'Product', 'Revenue', 'Units', 'Stock', 'Swap to']]
		for (const row of data.rows) {
			for (const cell of row) {
				rows.push([
					cell.slotLabel ?? String(cell.cellIndex),
					cell.productName ?? '',
					String(cell.revenueCents / 100),
					String(cell.units),
					cell.stockStatus ?? '',
					cell.swapPreview?.toProductName ?? '',
				])
			}
		}
		downloadCsv('cabinet-map.csv', rows)
	}

	const handleExportPng = async () => {
		if (!exportRef.current) return
		await captureElementAsPng(exportRef.current, 'cabinet-map.png')
	}

	useLayoutEffect(() => {
		if (metric !== 'pairs' || !gridRef.current || !data.pairs.length) {
			setLines([])
			return
		}

		const updateLines = () => {
			const grid = gridRef.current
			if (!grid) return
			const gridRect = grid.getBoundingClientRect()
			const nextLines: LineSegment[] = []

			for (const pair of data.pairs) {
				if (pair.productA.cellIndex == null || pair.productB.cellIndex == null) continue
				const aEl = cellRefs.current.get(pair.productA.cellIndex)
				const bEl = cellRefs.current.get(pair.productB.cellIndex)
				if (!aEl || !bEl) continue

				const aRect = aEl.getBoundingClientRect()
				const bRect = bEl.getBoundingClientRect()
				nextLines.push({
					key: `pair-${pair.index}`,
					x1: aRect.left + aRect.width / 2 - gridRect.left,
					y1: aRect.top + aRect.height / 2 - gridRect.top,
					x2: bRect.left + bRect.width / 2 - gridRect.left,
					y2: bRect.top + bRect.height / 2 - gridRect.top,
					color: pair.color,
					dashed: pair.placement !== 'adjacent',
				})
			}

			setLines(nextLines)
		}

		updateLines()
		const observer = new ResizeObserver(updateLines)
		observer.observe(gridRef.current)
		return () => observer.disconnect()
	}, [data.pairs, data.rows, metric])

	return (
		<Box
			sx={{
				mt: 1.5,
				p: 1.25,
				borderRadius: 1.5,
				border: '1px solid',
				borderColor: 'divider',
				bgcolor: 'background.default',
			}}
		>
			<Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 1 }}>
				<Box>
					<Typography sx={{ fontWeight: 600, fontSize: '0.8125rem' }} variant="body2">
						{title}
					</Typography>
					{subtitle ? (
						<Typography color="text.secondary" sx={{ fontSize: '0.7rem' }} variant="caption">
							{subtitle}
						</Typography>
					) : null}
				</Box>
				<ToggleButtonGroup
					exclusive
					onChange={(_, value: MapMetric | null) => {
						if (value) setMetric(value)
					}}
					size="small"
					value={metric}
				>
					{hasSales ? (
						<>
							<ToggleButton sx={{ px: 1, py: 0.25, fontSize: '0.7rem', lineHeight: 1.4 }} value="revenue">
								Revenue
							</ToggleButton>
							<ToggleButton sx={{ px: 1, py: 0.25, fontSize: '0.7rem', lineHeight: 1.4 }} value="units">
								Units
							</ToggleButton>
						</>
					) : null}
					{showIssuesMode ? (
						<ToggleButton sx={{ px: 1, py: 0.25, fontSize: '0.7rem', lineHeight: 1.4 }} value="issues">
							Issues
						</ToggleButton>
					) : null}
					{showSwapsMode ? (
						<ToggleButton sx={{ px: 1, py: 0.25, fontSize: '0.7rem', lineHeight: 1.4 }} value="swaps">
							Swaps
						</ToggleButton>
					) : null}
					{showPairsMode ? (
						<ToggleButton sx={{ px: 1, py: 0.25, fontSize: '0.7rem', lineHeight: 1.4 }} value="pairs">
							Pairs
						</ToggleButton>
					) : null}
				</ToggleButtonGroup>
				<Stack direction="row" spacing={0.25}>
					<Tooltip title="Export CSV">
						<IconButton aria-label="Export map CSV" onClick={handleExportCsv} size="small">
							<DownloadIcon size={16} />
						</IconButton>
					</Tooltip>
					<Tooltip title="Export image">
						<IconButton aria-label="Export map image" onClick={() => void handleExportPng()} size="small">
							<ImageIcon size={16} />
						</IconButton>
					</Tooltip>
				</Stack>
			</Stack>

			<Box ref={exportRef}>
				<Box ref={gridRef} sx={{ position: 'relative' }}>
					{metric === 'pairs' && lines.length ? (
						<Box
							component="svg"
							sx={{
								position: 'absolute',
								inset: 0,
								width: '100%',
								height: '100%',
								pointerEvents: 'none',
								overflow: 'visible',
								zIndex: 1,
							}}
						>
							{lines.map((line) => (
								<line
									key={line.key}
									stroke={line.color}
									strokeDasharray={line.dashed ? '5 4' : undefined}
									strokeLinecap="round"
									strokeWidth={2}
									x1={line.x1}
									x2={line.x2}
									y1={line.y1}
									y2={line.y2}
									opacity={0.85}
								/>
							))}
						</Box>
					) : null}

					<Stack spacing={0.75} sx={{ position: 'relative', zIndex: 2 }}>
						{data.rows.map((row, rowIndex) => (
							<Stack
								key={`heatmap-row-${rowIndex}`}
								direction="row"
								spacing={0.5}
								sx={{ flexWrap: 'nowrap', pb: 0.25 }}
							>
								{row.map((cell) => {
									const primaryPair = cell.pairIndices.length ? data.pairs[cell.pairIndices[0]] : undefined
									const isPairsMode = metric === 'pairs'
									const isIssuesMode = metric === 'issues'
									const isSwapsMode = metric === 'swaps'
									const salesMetric = metric === 'revenue' || metric === 'units' ? metric : 'revenue'
									const value = cellMetricValue(cell, salesMetric)
									const intensity = (metric === 'revenue' || metric === 'units') && maxValue > 0 ? value / maxValue : 0

									let bg = heatmapCellBackground(intensity)
									if (isPairsMode && primaryPair) bg = pairCellBackground(primaryPair.color)
									if (isIssuesMode) {
										if (cell.stockStatus === 'out') bg = 'hsla(0, 72%, 94%, 1)'
										else if (cell.stockStatus === 'low') bg = 'hsla(36, 90%, 92%, 1)'
										else if (cell.visionRisk) bg = 'hsla(28, 90%, 93%, 1)'
										else bg = 'var(--mui-palette-action-hover)'
									}
									if (isSwapsMode && cell.swapPreview) bg = 'hsla(145, 45%, 92%, 1)'

									const color =
										(metric === 'revenue' || metric === 'units') && intensity > 0.55
											? heatmapCellTextColor(intensity)
											: 'var(--mui-palette-text-primary)'
									const metricLabel = cellMetricLabel(cell, salesMetric)
									const tooltipParts = [cell.slotLabel ?? `Cell ${cell.cellIndex}`, cell.productName ?? 'Empty slot']
									if (metric === 'revenue' || metric === 'units') {
										tooltipParts.push(metricLabel)
										if (metric === 'revenue' && cell.units > 0) tooltipParts.push(`${cell.units} units`)
										if (metric === 'units' && cell.revenueCents > 0) {
											tooltipParts.push(formatHeatmapRevenue(cell.revenueCents))
										}
									}
									if (cell.stockStatus === 'out') tooltipParts.push('Out of stock')
									if (cell.stockStatus === 'low') tooltipParts.push(`Low stock (${cell.quantity ?? 0})`)
									if (cell.visionRisk && cell.visionRiskReason)
										tooltipParts.push(`Vision risk: ${cell.visionRiskReason}`)
									if (cell.swapPreview) {
										tooltipParts.push(
											`${cell.swapPreview.fromProductName ?? '—'} → ${cell.swapPreview.toProductName ?? '—'}`
										)
									}
									if (cell.pairIndices.length) {
										const pairLabels = cell.pairIndices
											.map((idx) => data.pairs[idx])
											.filter(Boolean)
											.map((pair) => `${pair.productA.productName} + ${pair.productB.productName}`)
										tooltipParts.push(`Pairs: ${pairLabels.join('; ')}`)
									}

									let borderColor = intensity > 0 ? 'transparent' : 'divider'
									if (primaryPair && (isPairsMode || cell.pairIndices.length)) borderColor = primaryPair.color
									if (isIssuesMode && cell.visionRisk) borderColor = '#ef6c00'
									if (isIssuesMode && cell.stockStatus === 'out') borderColor = '#d32f2f'
									if (isSwapsMode && cell.swapPreview) borderColor = '#2e7d32'

									return (
										<Tooltip key={`heatmap-cell-${cell.cellIndex}`} title={tooltipParts.join(' • ')}>
											<Box
												ref={(el: HTMLDivElement | null) => {
													if (el) cellRefs.current.set(cell.cellIndex, el)
													else cellRefs.current.delete(cell.cellIndex)
												}}
												sx={{
													flex: `${cell.slotWidth} 1 0`,
													minWidth: 0,
													minHeight: 56,
													px: 0.5,
													py: 0.75,
													borderRadius: 1,
													bgcolor: bg,
													color,
													border: '2px solid',
													borderColor,
													borderStyle: isIssuesMode && cell.visionRisk ? 'dashed' : 'solid',
													display: 'flex',
													flexDirection: 'column',
													justifyContent: 'space-between',
													overflow: 'hidden',
													boxShadow:
														primaryPair && metric === 'revenue' ? `inset 0 0 0 1px ${primaryPair.color}55` : undefined,
												}}
											>
												<Typography sx={{ fontSize: '0.625rem', fontWeight: 700, lineHeight: 1.2, opacity: 0.85 }}>
													{cell.slotLabel ?? `#${cell.cellIndex}`}
												</Typography>
												<Typography
													sx={{
														fontSize: '0.625rem',
														lineHeight: 1.2,
														opacity: cell.productName ? 0.95 : 0.55,
														overflow: 'hidden',
														textOverflow: 'ellipsis',
														whiteSpace: 'nowrap',
													}}
												>
													{cell.productName ? truncateProductName(cell.productName) : '—'}
												</Typography>
												{isPairsMode ? (
													cell.pairIndices.length ? (
														<Chip
															label={`${cell.pairIndices.length} pair${cell.pairIndices.length === 1 ? '' : 's'}`}
															size="small"
															sx={{
																height: 16,
																fontSize: '0.6rem',
																alignSelf: 'flex-start',
																bgcolor: primaryPair ? pairCellBackground(primaryPair.color, 0.35) : undefined,
															}}
														/>
													) : (
														<Typography sx={{ fontSize: '0.625rem', opacity: 0.45 }}>—</Typography>
													)
												) : isIssuesMode ? (
													<Typography sx={{ fontSize: '0.625rem', fontWeight: 600, lineHeight: 1.2 }}>
														{cell.stockStatus === 'out'
															? 'OOS'
															: cell.stockStatus === 'low'
																? 'Low'
																: cell.visionRisk
																	? 'Vision'
																	: 'OK'}
													</Typography>
												) : isSwapsMode ? (
													cell.swapPreview ? (
														<Typography sx={{ fontSize: '0.625rem', fontWeight: 600, lineHeight: 1.2 }}>
															{truncateProductName(cell.swapPreview.fromProductName ?? '—', 10)} →
														</Typography>
													) : (
														<Typography sx={{ fontSize: '0.625rem', opacity: 0.45 }}>—</Typography>
													)
												) : (
													<Typography sx={{ fontSize: '0.6875rem', fontWeight: 600, lineHeight: 1.2 }}>
														{value > 0 ? metricLabel : '—'}
													</Typography>
												)}
											</Box>
										</Tooltip>
									)
								})}
							</Stack>
						))}
					</Stack>
				</Box>

				{metric === 'revenue' || metric === 'units' ? (
					hasSales ? (
						<Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
							<Typography color="text.secondary" sx={{ fontSize: '0.65rem' }} variant="caption">
								Low
							</Typography>
							<Box
								sx={{
									flex: 1,
									height: 8,
									borderRadius: 1,
									background: `linear-gradient(to right, ${heatmapCellBackground(0)}, ${heatmapCellBackground(1)})`,
								}}
							/>
							<Typography color="text.secondary" sx={{ fontSize: '0.65rem' }} variant="caption">
								High
							</Typography>
						</Stack>
					) : null
				) : null}

				{metric === 'issues' && data.visionWarnings.length ? (
					<Stack spacing={0.5} sx={{ mt: 1.25 }}>
						<Typography sx={{ fontSize: '0.7rem', fontWeight: 600 }} variant="caption">
							Vision risks (adjacent look-alikes)
						</Typography>
						{data.visionWarnings.map((warning) => (
							<Typography
								key={`${warning.slotA}-${warning.slotB}`}
								color="text.secondary"
								sx={{ fontSize: '0.6875rem' }}
								variant="caption"
							>
								{warning.slotA} + {warning.slotB}: {warning.productAName} / {warning.productBName}
							</Typography>
						))}
					</Stack>
				) : null}

				{metric === 'swaps' && data.swaps.length ? (
					<Stack spacing={0.5} sx={{ mt: 1.25 }}>
						<Typography sx={{ fontSize: '0.7rem', fontWeight: 600 }} variant="caption">
							Proposed swaps
						</Typography>
						{data.swaps.map((swap) => (
							<Box key={`swap-${swap.cellIndex}`}>
								<Typography color="text.secondary" sx={{ fontSize: '0.6875rem' }} variant="caption">
									{swap.slotLabel ?? `#${swap.cellIndex}`}: {swap.fromProductName ?? '—'} → {swap.toProductName ?? '—'}
									{swap.currentRevenueCents != null && swap.suggestedRevenueCents != null
										? ` • $${(swap.currentRevenueCents / 100).toFixed(2)} → $${(swap.suggestedRevenueCents / 100).toFixed(2)}`
										: ''}
								</Typography>
								{swap.rationale ? (
									<Typography color="text.secondary" sx={{ fontSize: '0.625rem', opacity: 0.85 }} variant="caption">
										{swap.rationale}
									</Typography>
								) : null}
							</Box>
						))}
					</Stack>
				) : null}

				{showPairsMode ? (
					<Stack spacing={0.75} sx={{ mt: 1.25 }}>
						<Typography sx={{ fontSize: '0.7rem', fontWeight: 600 }} variant="caption">
							Top pairs on this map
						</Typography>
						{data.pairs.map((pair) => (
							<PairLegendItem key={`pair-legend-${pair.index}`} pair={pair} />
						))}
					</Stack>
				) : null}
			</Box>
		</Box>
	)
}
